<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Slider from "primevue/slider";

const emit = defineEmits(["update:range-value"]);

const props = defineProps({
    range: {
        type: Array<number>,
        default: [20, 80],
    },
    title: {
        type: String,
        default: "",
    },
    step: {
        type: Number,
        default: 1,
    },
    adjectives: {
        type: Array<string>,
        default: () => new Array(12).fill(""),
    },
    sliderClass: {
        type: String,
        default: "",
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const sliderRange = ref(props.range);

/**
 * The "<dimension>-slider" token of sliderClass, used to color the dimension badge on phones. The other
 * tokens (e.g. the w-12 width utility) belong to the slider column only and must not leak onto the root.
 */
const dimensionClass = computed(() => props.sliderClass.split(" ").find((token) => token.endsWith("-slider")) ?? "");

/**
 * Checks if the given range is within the range of the slider handles
 * @param min
 * @param max
 */
const isInRange = (min: number, max: number): boolean => {
    return sliderRange.value[0] <= max && sliderRange.value[1] >= min;
};

/**
 * Watches for changes in the sliderRange and prevents overlap of the handles
 */
watch(sliderRange, (newValue) => {
    if (newValue[0] > newValue[1] - 5) {
        sliderRange.value = [newValue[1] - 5, newValue[1]];
    } else if (newValue[1] < newValue[0]) {
        sliderRange.value = [newValue[0], newValue[0] + 5];
    }
    emit("update:range-value", newValue);
});
</script>

<template>
    <div :class="['slider-container', dimensionClass]">
        <h3 class="title">{{ title }}</h3>
        <div :class="['slider-adjectives-container', sliderClass]">
            <Slider
                :id="`dimension-slider-${title}`"
                v-model="sliderRange"
                range
                orientation="vertical"
                :step="step"
                :class="['slider']"
                :disabled="disabled"
                :pt="{
                    startHandler: { tabindex: disabled ? -1 : 0 },
                    endHandler: { tabindex: disabled ? -1 : 0 }
                }"
            />
            <div class="adjectives" :id="`dimension-slider-adjectives-${title}`">
                <div class="adjectives-set" :class="{ highlight: isInRange(75, 100) }" style="top: 12.5%">
                    <span class="adjective" v-if="adjectives[0]">{{ adjectives[0] }}</span>
                    <span class="adjective" v-if="adjectives[1]">{{ adjectives[1] }}</span>
                    <span class="adjective" v-if="adjectives[2]">{{ adjectives[2] }}</span>
                </div>
                <div class="adjectives-set" :class="{ highlight: isInRange(50, 75) }" style="top: 37.5%">
                    <span class="adjective" v-if="adjectives[3]">{{ adjectives[3] }}</span>
                    <span class="adjective" v-if="adjectives[4]">{{ adjectives[4] }}</span>
                    <span class="adjective" v-if="adjectives[5]">{{ adjectives[5] }}</span>
                </div>
                <div class="adjectives-set" :class="{ highlight: isInRange(25, 50) }" style="top: 62.5%">
                    <span class="adjective" v-if="adjectives[6]">{{ adjectives[6] }}</span>
                    <span class="adjective" v-if="adjectives[7]">{{ adjectives[7] }}</span>
                    <span class="adjective" v-if="adjectives[8]">{{ adjectives[8] }}</span>
                </div>
                <div class="adjectives-set" :class="{ highlight: isInRange(0, 25) }" style="top: 87.5%">
                    <span class="adjective" v-if="adjectives[9]">{{ adjectives[9] }}</span>
                    <span class="adjective" v-if="adjectives[10]">{{ adjectives[10] }}</span>
                    <span class="adjective" v-if="adjectives[11]">{{ adjectives[11] }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.slider-container {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.slider-adjectives-container {
    display: flex;
    position: relative;
    justify-content: space-around;
    margin: 0 8.5rem 0 auto;
}

.adjectives {
    display: flex;
    flex-direction: column;
    align-items: end;
}

.adjectives-set {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    position: absolute;
    left: 30%;
    right: 0;
    margin-left: 10px;
    transform: translateY(-50%);
    background-color: white;
    width: fit-content;
    border-radius: 10px !important;
}

.adjective {
    color: lightgrey;
    font-size: 0.7em; /* Adjust this value to change the text size */
    padding: 0.1em 0.3em;
    white-space: nowrap;
}

.slider {
    width: 5rem;
    height: 22rem;
    padding: 0 5px;
    margin-right: 2rem;
    border-radius: 10px !important;
    transition: transform 0.3s;
    align-self: flex-start;
}

.dominant-slider .slider {
    background-color: rgb(255, 208, 208);
    --p-slider-range-background: #ff103a !important;
    --p-slider-handle-hover-background: #ff103a !important;
}

.dominant-slider .highlight * {
    color: black;
    background-color: rgb(255, 208, 208);
}

.initiative-slider .slider {
    background-color: rgb(249, 245, 189);
    --p-slider-range-background: #ffc400 !important;
    --p-slider-handle-hover-background: #ffc400 !important;
}

.initiative-slider .highlight * {
    color: black;
    background-color: rgb(249, 245, 189);
}

.steady-slider .slider {
    background-color: rgb(182, 231, 204);
    --p-slider-range-background: #00875d !important;
    --p-slider-handle-hover-background: #00875d !important;
}

.steady-slider .highlight * {
    color: black;
    background-color: rgb(187, 248, 187);
}

.cautious-slider .slider {
    background-color: #c0d5e6;
    --p-slider-range-background: #0066c1 !important;
    --p-slider-handle-hover-background: #0066c1 !important;
}

.cautious-slider .highlight * {
    color: black;
    background-color: #c0d5e6;
}

.highlight * {
    font-weight: bold;
}

/* Phones: the block spans the full width — colored dimension badge on top, slider on the left, the
   adjectives fill the remaining width. Separators at 25/50/75 % mark the four levels the highlight
   logic (isInRange) works with, replacing the desktop diagram's reference lines. */
@media screen and (max-width: 767px) {
    .slider-container {
        align-items: stretch;
        width: 100%;
    }
    .title {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        align-self: flex-start;
        width: 2rem;
        height: 2rem;
        margin: 0 0 0.5rem;
        border-radius: 50%;
        color: #fff;
        font-size: 1rem;
    }
    .dominant-slider .title {
        background-color: #ff103a;
    }
    .initiative-slider .title {
        background-color: #ffc400;
    }
    .steady-slider .title {
        background-color: #00875d;
    }
    .cautious-slider .title {
        background-color: #0066c1;
    }
    .slider-adjectives-container {
        width: 100%;
        margin: 0;
        justify-content: flex-start;
    }
    .slider {
        width: 2.5rem;
        height: 18rem;
        margin-right: 0.75rem;
        --p-slider-handle-width: 1.5rem;
        --p-slider-handle-height: 1.5rem;
    }
    .adjectives {
        position: relative;
        flex: 1;
        min-width: 0;
        align-items: stretch;
        background-image:
            linear-gradient(to bottom, transparent calc(25% - 1px), #c9c9c9 calc(25% - 1px), #c9c9c9 25%, transparent 25%),
            linear-gradient(to bottom, transparent calc(50% - 1px), #c9c9c9 calc(50% - 1px), #c9c9c9 50%, transparent 50%),
            linear-gradient(to bottom, transparent calc(75% - 1px), #c9c9c9 calc(75% - 1px), #c9c9c9 75%, transparent 75%);
    }
    .adjectives-set {
        left: 0;
        right: 0;
        width: auto;
        margin-left: 0;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: flex-start;
        column-gap: 0.25rem;
        padding: 0.15rem 0.35rem;
    }
    .adjective {
        font-size: 0.85em;
        color: #666;
        white-space: normal;
    }
}
</style>
